import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  GoogleMap,
  InfoWindow,
  Marker,
  useLoadScript,
} from "@react-google-maps/api";
import {
  generateColor,
  getInitialName,
  getMapCenter,
  getMqttData,
  stringAvatar,
} from "../../../utilities/commonFunctions";
import classes from "./index.module.css";
import "../../../assets/css/notModule.css";
import {
  Avatar,
  Badge,
  Button,
  Typography,
} from "@mui/material";
import {
  authApi,
  getAuthorizationHeader,
} from "../../../api-calls/user-requests";
import { MAP_MARKER_TEAM } from "../../../api-calls/api-urls";
import { useNavigate } from "react-router-dom";
import FilterContainer from "./filterContainer";
import AlertBox from "../../../components/AlertBox";
import AddMemberModal from "../MyTeam/AddMemberModal";
import { useDispatch, useSelector } from "react-redux";
import { EditPenIcon } from "../../../components/Icons";
import moment from "moment";

const GOOGLE_MAPS_LIBRARIES = ["places"];

// var options = {
//   protocol: "wss",
//   keepalive: 20,
//   // clientId uniquely identifies client
//   // choose any string you wish
//   clientId: "mqttjs_" + Math.random().toString(16).substr(2, 8),
// };
// var client = mqtt.connect("mqtt://broker.hivemq.com:8000", options);

function Map() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeMarker, setActiveMarker] = useState(null);
  const [modletoggle, setModletoggle] = useState(false);
  const [activeUser, setActiveUser] = useState();
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const [showAlert, setShowAlert] = useState(false);
  const [error, setError] = useState();
  const [mapcenter, setMapcenter] = useState();
  const { markerList, teamMembersList, playerStatusFilter } = useSelector(
    (state) => state.allUsers
  );
  // Keep a ref to the latest teamMembersList so the polling interval can read
  // the current list without being listed as a React effect dependency (which
  // was causing the interval to be torn down on every MQTT position update).
  // Initialized to [] — the sync effect below keeps it current.
  const teamMembersRef = useRef([]);

  const CloseForm = () => {
    setModletoggle(false);
  };

  useEffect(() => {
    if (markerList && markerList?.length > 0) {
      getMapCenter(setMapcenter, markerList);

      // Hydrate teamMembersList with position data from markerList.
      // The marker API returns position for players with recent location data,
      // so we can populate positions before MQTT updates arrive.
      if (teamMembersList?.length > 0) {
        let hasChanges = false;
        const updatedList = teamMembersList.map((member) => {
          const marker = markerList.find(
            (m) =>
              m?.user_id?.toString() === member?.user_id?._id?.toString() ||
              m?._id?.toString() === member?.user_id?._id?.toString()
          );
          if (marker) {
            const newStatus = marker.status || member.status || "checkout";
            
            // Parse position from marker (API)
            const parsedPosition = marker.position
              ? {
                  lat: parseFloat(marker.position.lat),
                  lng: parseFloat(marker.position.lng),
                }
              : undefined;
            const apiPosition = (parsedPosition && !isNaN(parsedPosition.lat) && !isNaN(parsedPosition.lng)) 
              ? parsedPosition 
              : undefined;

            // Decision logic:
            // 1. If API has a position and member doesn't -> Update (initial load or new activity)
            // 2. If API has no position and member does -> Clear (stale data from previous date or checkout)
            //    Exception: If member position is from MQTT (Today), we might be clearing it.
            //    However, if marker.status is checkout and there's no location for today, 
            //    it's safe to clear.
            
            const positionNeedsUpdate = JSON.stringify(apiPosition) !== JSON.stringify(member.position);
            const statusNeedsUpdate = newStatus !== member.status;

            if (positionNeedsUpdate || statusNeedsUpdate) {
              hasChanges = true;
              return {
                ...member,
                ...marker,
                position: apiPosition,
                status: newStatus,
              };
            }
          }
          return member;
        });
        if (hasChanges) {
          dispatch({ type: "TEAM_MEMBERS_LIST", payload: updatedList });
        }
      }
    }
  }, [markerList, teamMembersList, dispatch]);

  // Keep the ref in sync so the interval always uses the latest list.
  useEffect(() => {
    teamMembersRef.current = teamMembersList;
  }, [teamMembersList]);

  // Fire the initial Livedata request and set up a 2-minute refresh interval.
  // Intentionally does NOT depend on teamMembersList directly — the ref above
  // keeps the value current without causing the interval to be torn down and
  // recreated on every single MQTT position update (which was the previous bug).
  useEffect(() => {
    if (!markerList?.length || !window.location.pathname.includes("map")) return;

    console.log("[Map] starting Livedata polling — markerList:", markerList.length);
    getMqttData(userInfo?._id, teamMembersRef.current, dispatch, "map");

    const intervalId = setInterval(() => {
      console.log("[Map] interval Livedata refresh");
      getMqttData(userInfo?._id, teamMembersRef.current, dispatch, "map");
    }, 2 * 60 * 1000); // 2 minutes

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markerList, userInfo?._id, dispatch]);

  const handleActiveMarker = useCallback(
    (marker) => {
      if (marker.name === activeMarker) {
        return;
      }
      setActiveMarker(marker.name);
      setActiveUser(marker);
      setMapcenter(marker.position);
    },
    [activeMarker]
  );

  const getMapMArkerList = useCallback(
    async (search, date) => {
      try {
        const userEntry = {};
        const dateParam = date ? moment(date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
        const { data } = await authApi.get(
          `${MAP_MARKER_TEAM}/${userInfo?._id}?search=${search ? search : ""}&date=${dateParam}`,
          {
            headers: { Authorization: getAuthorizationHeader() },
            data: userEntry,
          }
        );
        dispatch({ type: "MAP_MARKER_LIST", payload: data });
      } catch (error) {
        if (error.response && error.response.status === 400) {
          console.log(error.response);
          setShowAlert(true);
          setError(error.response.data.message);
        }
      }
    },
    [userInfo?._id, dispatch]
  );

  const dateObject = useCallback(
    (date, search) => {
      getMapMArkerList(search, date);
    },
    [getMapMArkerList]
  );

  // Sets default React state

  const isMarkerListEmpty = markerList?.length === 0;

  useEffect(() => {
    try {
      getMapMArkerList();
    } catch (error) {
      if (error.response && error.response.status === 400) {
        console.log(error.response);
        setShowAlert(true);
        setError(error.response.data.message);
      }
    }
  }, [isMarkerListEmpty, getMapMArkerList]);

  const handleViewRoute = (item) => {
    console.log(item);
    navigate(`/maps/view-route/${item._id}`);
  };

  const getMarkerSVG = (name, color) => {
    const isSelectedMarker = activeMarker && activeMarker === name;
    const fillColor = color;
    const strokeColor = isSelectedMarker ? color : "#000";
    const textColor = "#fff";

    return `<svg width="44px" height="52px" viewBox="0 0 44 52" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="22" cy="12" r="8" fill="${fillColor}" stroke="${strokeColor}" stroke-width="2"/>
        <path d="M 6 48 C 6 30, 8 24, 22 24 C 36 24, 38 30, 38 48 Z"
          fill="${fillColor}" stroke="${strokeColor}" stroke-width="2" stroke-linejoin="round"/>
        <text x="22" y="38" dominant-baseline="middle" text-anchor="middle"
          fill="${textColor}" font-size="11px" font-weight="bold" font-family="Arial">
          ${getInitialName(name).toUpperCase()}
        </text>
      </svg>`;
  };

  // Default center (fallback)
  const defaultCenter = useMemo(() => ({ lat: 20.5937, lng: 78.9629 }), []);

  // Set current location as map center if no markers
  useEffect(() => {
    if (!mapcenter && (!markerList || markerList.length === 0)) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setMapcenter({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          () => {
            setMapcenter(defaultCenter); // fallback if denied
          }
        );
      } else {
        setMapcenter(defaultCenter); // fallback if not supported
      }
    }
  }, [mapcenter, markerList, defaultCenter]);

  return (
    <>
      <div style={{ width: "100%", position: "relative" }}>
        <FilterContainer
          dateObject={dateObject}
          teamMembers={teamMembersList}
          onSelectMember={handleActiveMarker}
        />
      </div>
      <GoogleMap
        center={mapcenter || defaultCenter}
        zoom={12}
        onClick={() => setActiveMarker(null)}
        mapContainerStyle={{ height: "100%", marginTop: "0px" }}
      >
        {teamMembersList
          .filter((m) => playerStatusFilter?.includes("all") || playerStatusFilter?.includes(m.status))
          .map((location, index) => {
            if (
              !location?.position ||
              isNaN(location.position.lat) ||
              isNaN(location.position.lng)
            ) {
              return null;
            }
            return (
          <Marker
            key={index}
            position={location?.position}
            onClick={() => handleActiveMarker(location)}
            icon={{
              url: `data:image/svg+xml;utf-8,${encodeURIComponent(
                getMarkerSVG(location.name, location?.department?.colour)
              )}`,
              anchor: new window.google.maps.Point(22, 50),
              scaledSize: new window.google.maps.Size(44, 52),
            }}
          >
            {activeMarker === location.name ? (
              <InfoWindow
                position={activeUser?.position || location?.position}
                onCloseClick={() => setActiveMarker(null)}
              >
                <div className={classes.infoWindowContainer}>
                  <div className={classes.infoWindowContainerHEader}>
                    <div>
                      <Badge
                        color={
                          location.status === "checkin" ? "success" : "default"
                        }
                        badgeContent=" "
                        variant="dot"
                        overlap="circular"
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                      >
                        {location &&
                        location?.user_id &&
                        location?.user_id?.profile_pic &&
                        location?.user_id?.profile_pic?.filename ? (
                          <Avatar
                            alt={location.name}
                            src={`${process.env.REACT_APP_IMAGE_URL}/${location?.user_id?.profile_pic?.destination}${location?.user_id?.profile_pic?.filename}`}
                            sx={{ width: 40, height: 40 }}
                          />
                        ) : (
                          <Avatar
                            {...stringAvatar(location.name)}
                            sx={{
                              bgcolor: location?.department?.colour
                                ? location?.department?.colour
                                : `${generateColor(location.name)}`,
                            }}
                          />
                        )}
                      </Badge>
                    </div>
                    <div className={classes.nameContainer}>
                      <Typography className={classes.cardLabelText}>
                        Team Member
                      </Typography>
                      <Typography className={classes.cardImpText}>
                        {location.name}
                      </Typography>
                      <Button
                        variant="outlined"
                        style={{ textTransform: "none", marginTop: "10px" }}
                        onClick={() => handleViewRoute(location)}
                      >
                        View route
                      </Button>
                    </div>
                    <div>
                      <span onClick={() => setModletoggle(true)}>
                        <EditPenIcon />
                      </span>
                      {/* <RiDeleteBin6Line /> */}
                    </div>
                  </div>
                  <div
                    className={`${classes.infoWindowContainerHEader} ${classes.infoSubHeader}`}
                  >
                    <div>
                      <Typography className={classes.cardLabelText}>
                        Team
                      </Typography>
                      <Typography className={classes.cardImpText}>
                        {activeUser?.team_name || location?.team_name || "N/A"}
                      </Typography>
                    </div>
                    <div>
                      <Typography className={classes.cardLabelText}>
                        DepartMent
                      </Typography>
                      <Typography
                        className={`${classes.cardImpText} ${classes.departmentCard}`}
                      >
                        <Avatar
                          sx={{
                            bgcolor: `${
                              activeUser?.department?.colour ||
                              location?.department?.colour ||
                              "#ccc"
                            }`,
                            width: "13px",
                            height: "13px",
                            marginRight: "5px",
                          }}
                        >
                          {" "}
                        </Avatar>{" "}
                        |{" "}
                        {activeUser?.department?.department_name ||
                          location?.department?.department_name ||
                          "N/A"}
                      </Typography>
                    </div>
                    <div>
                      <Typography className={classes.cardLabelText}>
                        Battery
                      </Typography>
                      <Typography className={classes.cardImpText}>
                        {location && location.batteryLevel
                          ? `${location?.batteryLevel}%`
                          : `offline`}
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.piechartContainer}>
                    <div className={classes.infoWindowContainerHEader}>
                      <Typography className={classes.piechartHeading}>
                        Total Task
                      </Typography>
                      <Typography
                        className={`${classes.linkBtntoAssign} ${classes.piechartHeading}`}
                        onClick={() => navigate("/location/create-location")}
                      >
                        Assign a Task
                      </Typography>
                    </div>
                    <div
                      className={`${classes.infoWindowContainerHEader} ${classes.piechartHeading1}`}
                    >
                      <div>
                        <svg
                          aria-hidden="true"
                          focusable="false"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 44 44"
                          className={classes.piechartSvg}
                        >
                          <circle
                            cx="50%"
                            cy="50%"
                            fill="transparent"
                            r="15.909091"
                            stroke="#eee"
                            strokeWidth={10}
                          />
                          <circle
                            show-tip="ctDonutTip"
                            className="ct-donut-slice"
                            cx="50%"
                            cy="50%"
                            fill="transparent"
                            r="15.909091"
                            stroke="#1273B8" // Completed color
                            strokeDasharray={`${
                              ((activeUser?.location_datas?.completed || 0) /
                                (activeUser?.location_datas?.total || 1)) *
                              100
                            } ${
                              ((activeUser?.location_datas?.total || 1) -
                                (activeUser?.location_datas?.completed || 0) /
                                  (activeUser?.location_datas?.total || 1)) *
                              100
                            }`}
                            strokeWidth={10}
                            strokeDashoffset={0}
                            slice-title="Completed"
                            percent-value={11}
                            usage={11}
                            total-val={20}
                          >
                            <animate
                              attributeName="stroke-dashoffset"
                              from={100}
                              to={0}
                              dur="0.5s"
                            />
                          </circle>
                          <circle
                            show-tip="ctDonutTip"
                            className="ct-donut-slice"
                            cx="50%"
                            cy="50%"
                            fill="transparent"
                            r="15.909091"
                            stroke="#240051" // Pending color
                            strokeDasharray={`${
                              ((activeUser?.location_datas?.pending || 0) /
                                (activeUser?.location_datas?.total || 1)) *
                              100
                            } ${
                              ((activeUser?.location_datas?.total || 1) -
                                (activeUser?.location_datas?.pending || 0) /
                                  (activeUser?.location_datas?.total || 1)) *
                              100
                            }`}
                            strokeWidth={10}
                            strokeDashoffset={
                              -((activeUser?.location_datas?.completed || 0) /
                                (activeUser?.location_datas?.total || 1)) *
                              100
                            } // Adjusted to start from the end point of the second slice
                            slice-title="Pending"
                            percent-value={2}
                            usage={2}
                            total-val={20}
                          >
                            <animate
                              attributeName="stroke-dashoffset"
                              from={100}
                              to={-100} // Adjusted to start from the end point of the second slice
                              dur="0.5s"
                            />
                          </circle>
                          <circle
                            show-tip="ctDonutTip"
                            className="ct-donut-slice"
                            cx="50%"
                            cy="50%"
                            fill="transparent"
                            r="15.909091"
                            stroke="#C52A38" // Rejected color
                            strokeDasharray={`${
                              ((activeUser?.location_datas?.rejected || 0) /
                                (activeUser?.location_datas?.total || 1)) *
                              100
                            } ${
                              ((activeUser?.location_datas?.total || 1) -
                                (activeUser?.location_datas?.rejected || 0) /
                                  (activeUser?.location_datas?.total || 1)) *
                              100
                            }`}
                            strokeWidth={10}
                            strokeDashoffset={
                              -(((activeUser?.location_datas?.completed || 0) +
                                (activeUser?.location_datas?.pending || 0)) /
                                (activeUser?.location_datas?.total || 1)) *
                              100
                            } // Adjusted to start from the end point of the second slice
                            slice-title="Rejected"
                            percent-value={7}
                            usage={7}
                            total-val={20}
                          >
                            <animate
                              attributeName="stroke-dashoffset"
                              from={100}
                              to={-100} // Adjusted to start from the end point of the second slice
                              dur="0.5s"
                            />
                          </circle>
                          <circle
                            cx="50%"
                            cy="50%"
                            fill="#FFFFFF"
                            r={16}
                          ></circle>
                          <text x="50%" y="55%" className="ct-donut-center">
                            {activeUser?.location_datas?.total || 0}
                          </text>
                        </svg>
                        <div className="ct-tip" />
                      </div>
                      <div
                        className={`${classes.infoWindowContainerHEader} ${classes.piechartSubContainer}`}
                      >
                        <div>
                          <Typography
                            className={`${classes.cardImpText} ${classes.departmentCard}`}
                          >
                            <Avatar
                              sx={{
                                bgcolor: `#1273B8`,
                                width: "13px",
                                height: "13px",
                                marginRight: "5px",
                              }}
                            >
                              {" "}
                            </Avatar>{" "}
                            {activeUser?.location_datas?.completed || 0}
                          </Typography>
                          <Typography>Completed</Typography>
                        </div>
                        <div>
                          <Typography
                            className={`${classes.cardImpText} ${classes.departmentCard}`}
                          >
                            <Avatar
                              sx={{
                                bgcolor: `#240051`,
                                width: "13px",
                                height: "13px",
                                marginRight: "5px",
                              }}
                            >
                              {" "}
                            </Avatar>{" "}
                            {activeUser?.location_datas?.pending || 0}
                          </Typography>
                          <Typography>Pending</Typography>
                        </div>
                        <div>
                          <Typography
                            className={`${classes.cardImpText} ${classes.departmentCard}`}
                          >
                            <Avatar
                              sx={{
                                bgcolor: `#C52A38`,
                                width: "13px",
                                height: "13px",
                                marginRight: "5px",
                              }}
                            >
                              {" "}
                            </Avatar>{" "}
                            {activeUser?.location_datas?.rejected || 0}
                          </Typography>
                          <Typography>Rejected</Typography>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </InfoWindow>
            ) : null}
          </Marker>
          );
        })}
        {modletoggle && activeUser ? (
          <AddMemberModal
            modalClose={CloseForm}
            user={activeUser}
            name={"edit-from-map"}
          />
        ) : null}
        {showAlert && error && (
          <AlertBox type="error" message={error} stateName={setShowAlert} />
        )}
      </GoogleMap>
    </>
  );
}

export default function GoogleApiWrapper() {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  return isLoaded ? <Map /> : null;
}
